import { useRef } from "react";

import { AttributeType, IAttribute, IRelationObject } from "@interfaces/attribute.interface";
import { DatePicker, Input, LocationAutocomplete, MaskInput } from "@ui/FormElements";
import { isMaskInputVariant } from "@ui/FormElements/MaskInput.config";
import { SearchableSelect } from "@ui/Select";

import {
  DEFAULT_CURRENCY_SYMBOL,
  DROPDOWN_ATTRIBUTE_TYPES,
  isRelationAttribute,
} from "./constants";
import { RelationAttributeInput } from "./RelationAttribute";
import {
  formatAttributeDate,
  getAttributeInputType,
  getAttributePlaceholder,
  parseAttributeDate,
} from "./utils";

type Option = { value: string; label: string };

interface AttributeFieldProps {
  attribute: IAttribute;
  // For dropdown types this is comma-joined option ids.
  value: string;
  onChange: (value: string) => void;
  // Fires when the value is committed (input blur / select change) — used for inline save.
  onCommit?: (value: string) => void;
  disabled?: boolean;
  isInvalid?: boolean;
  errorMessage?: string;
  // Prefix shown on CURRENCY attributes.
  currencySymbol?: string;
  // RELATION only: the tagged records; `value` is unused for these fields.
  relations?: IRelationObject[];
  onRelationsChange?: (next: IRelationObject[]) => void;
  // RELATION only: the settled set on close — one write per batch.
  onRelationsSave?: (next: IRelationObject[]) => void;
  // RELATION only: records the search must not offer.
  excludedRelationIds?: string[];
}

export function AttributeField({
  attribute,
  value,
  onChange,
  onCommit,
  disabled,
  isInvalid,
  errorMessage,
  currencySymbol = DEFAULT_CURRENCY_SYMBOL,
  relations,
  onRelationsChange,
  onRelationsSave,
  excludedRelationIds,
}: AttributeFieldProps) {
  const placeholder = getAttributePlaceholder(attribute);
  // Value when the input gained focus — lets us skip committing an unchanged field on blur.
  const focusValueRef = useRef(value);

  const commitOnFocusChange = {
    onFocus: () => {
      focusValueRef.current = value;
    },
    onBlur: () => {
      if (value !== focusValueRef.current) onCommit?.(value);
    },
  };

  // For fields that emit a value only once it's settled (dropdowns, location), every change is a
  // commit — they can't use the focus/blur diff above, whose `value` is still the pre-edit one.
  const commit = (next: string) => {
    if (next === value) return;
    onChange(next);
    onCommit?.(next);
  };

  if (isRelationAttribute(attribute.type)) {
    return (
      <RelationAttributeInput
        attribute={attribute}
        value={relations ?? []}
        onChange={(next) => onRelationsChange?.(next)}
        onSave={(next) => onRelationsSave?.(next)}
        excludedIds={excludedRelationIds}
        disabled={disabled}
      />
    );
  }

  if (DROPDOWN_ATTRIBUTE_TYPES.includes(attribute.type)) {
    const options: Option[] = (attribute.dropdown_options ?? []).map((o) => ({
      value: o.id,
      label: o.title,
    }));
    const selectedIds = value ? value.split(",").filter(Boolean) : [];

    if (attribute.type === AttributeType.MULTISELECT) {
      return (
        <SearchableSelect<Option, true>
          isMulti
          isClearable
          className="w-full"
          placeholder={placeholder}
          isDisabled={disabled}
          options={options}
          value={options.filter((o) => selectedIds.includes(o.value))}
          onChange={(opts) => commit((opts as Option[]).map((o) => o.value).join(","))}
        />
      );
    }

    return (
      <SearchableSelect<Option>
        isClearable
        className="w-full"
        placeholder={placeholder}
        isDisabled={disabled}
        options={options}
        value={options.find((o) => o.value === value) ?? null}
        onChange={(opt) => commit((opt as Option | null)?.value ?? "")}
      />
    );
  }

  if (attribute.type === AttributeType.DATE) {
    return (
      <DatePicker
        selectedDate={parseAttributeDate(value)}
        placeholderText={placeholder}
        dateFormat="MM/dd/yyyy"
        strictParsing
        popperPlacement="bottom"
        disabled={disabled}
        inputProps={{ isInvalid, errorMessage }}
        onChange={(date) => commit(formatAttributeDate(date))}
      />
    );
  }

  if (attribute.type === AttributeType.LOCATION) {
    return (
      <LocationAutocomplete
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        isInvalid={isInvalid}
        errorMessage={errorMessage}
        onChange={commit}
      />
    );
  }

  if (isMaskInputVariant(attribute.type)) {
    return (
      <MaskInput
        variant={attribute.type}
        value={value}
        prefix={attribute.type === AttributeType.CURRENCY ? currencySymbol : undefined}
        disabled={disabled}
        isInvalid={isInvalid}
        errorMessage={errorMessage}
        onChange={onChange}
        {...commitOnFocusChange}
      />
    );
  }

  return (
    <Input
      isFullWidth
      type={getAttributeInputType(attribute.type)}
      value={value}
      placeholder={placeholder}
      disabled={disabled}
      isInvalid={isInvalid}
      errorMessage={errorMessage}
      onChange={(e) => onChange(e.target.value)}
      {...commitOnFocusChange}
    />
  );
}
